import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { TEMP_LANG_KEY } from '@rs/ui'
import { switchLanguage } from '@rs/ui/i18n'
import { SESSION_EXPIRED_EVENT } from '../shared/api.ts'

/**
 * ══ "TÔI LÀ AI" — KHÔNG GIỮ TOKEN NÀO Ở TRÌNH DUYỆT ════════════════════════════════════════════
 * Phiên nằm trong cookie HttpOnly do máy chủ đặt; trình duyệt chỉ hỏi `GET /api/v1/auth/me`.
 * Bản cũ giữ toàn bộ danh tính trong `localStorage` (A2/A3) — nay không còn.
 */
export interface Me {
  id: string
  email: string
  name: string
  role: string
  /** ⚠ HAI TRƯỜNG CỜ GIÁ VỐN (C10) — thiếu một trong hai là menu ẩn oan và bị đá ra oan. */
  azSeeVendorCost: boolean
  azSalesScope: 'own' | 'all'
  /**
   * Được chào giá theo GIÁ VỐN × % lời — bản cũ `canCostQuote` xét cờ này **hoặc** vai admin/GĐ.
   * ⚠ `MeDto` của máy chủ đã trả sẵn từ lâu; khai thiếu ở đây làm ô tick "được chào theo giá vốn"
   *   ở Danh mục ▸ Người dùng thành ô chết — cấp quyền xong màn báo giá vẫn khoá.
   */
  azQuoteFromCost?: boolean
  /**
   * Đang dùng MẬT KHẨU TẠM do admin/KTT cấp (E13 sửa 19/10/2026) — router vẽ màn ép đổi thay cho mọi màn, máy chủ chặn
   * mọi cửa khác. `SessionUser` của máy chủ luôn trả trường này; tuỳ chọn ở đây chỉ để các ca kiểm dựng `Me` cũ khỏi sửa.
   */
  mustChangePass?: boolean
  lang: 'vi' | 'en' | 'zh'
}

interface Ctx {
  me: Me | null
  loading: boolean
  /**
   * `/auth/me` hỏng vì lý do KHÁC 401 (429 hạn mức, 502 máy chủ đang khởi động, mất mạng) — mã lỗi
   * để hiện băng "chưa bị đăng xuất, đang thử lại". `null` = không có lỗi. Xem `fetchMe`.
   */
  checkError: string | null
  /** Phiên vừa hết GIỮA CHỪNG (401 từ một lệnh bất kỳ) — màn đăng nhập nói rõ vì sao (L18). */
  expired: boolean
  /** Thử lại `/auth/me` ngay (nút "Thử lại ngay" trên băng lỗi). */
  retry: () => void
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthCtx = createContext<Ctx | null>(null)

/**
 * Hỏi máy chủ "tôi là ai". Hàm THUẦN LẤY DỮ LIỆU, KHÔNG chạm state — để cả effect lúc dựng lẫn
 * `refresh()` dùng chung MỘT lời gọi, không chép hai bản dễ lệch nhau.
 *
 * ⚠ CHỈ 401 MỚI LÀ "CHƯA ĐĂNG NHẬP" (T35, soát 16/10). Bản trước gộp MỌI phản hồi không `ok` vào
 *   `null`: lượt đầu dính 429 (hạn mức 600 lệnh/phút/IP — cả văn phòng sau một NAT) hay 502 (máy chủ
 *   đang khởi động lại) là hiện ngay màn "Đăng nhập với FADO", trong khi cùng cookie gọi lại một giây
 *   sau là 200. Người dùng tưởng bị đăng xuất, bấm đăng nhập lại, tốn thêm một vòng SSO.
 *   Vẫn FAIL-CLOSED (CLAUDE.md §4): lỗi khác 401 KHÔNG mở ứng dụng, không đoán danh tính cũ — chỉ
 *   hiện băng lỗi + tự thử lại, thay cho màn đăng nhập sai sự thật.
 */
type MeResult = { kind: 'me'; me: Me } | { kind: 'anon' } | { kind: 'error'; status: string }

const fetchMe = async (): Promise<MeResult> => {
  try {
    const r = await fetch('/api/v1/auth/me')
    if (r.ok) return { kind: 'me', me: (await r.json()) as Me }
    if (r.status === 401) return { kind: 'anon' }
    return { kind: 'error', status: String(r.status) }
  } catch (e) {
    return { kind: 'error', status: e instanceof Error ? e.message : String(e) }
  }
}

/** Nhịp tự thử lại khi `/auth/me` lỗi khác 401: 2 · 4 · 8 · 16 · 30 giây rồi dừng, chờ người bấm. */
const RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 30_000]

/**
 * ══ NGÔN NGỮ CỦA PHIÊN → GIAO DIỆN (D03b §7.3, Task 23) ═══════════════════════════════════════
 * `me.lang` đến từ `session.lang`, mà `session.lang` được nạp từ `app_user.lang`/`vendor_user.lang`
 * lúc đăng nhập (0014 + `ssoLogin`/`vendorLogin`). KHÔNG áp nó vào i18next ở đây thì cả chuỗi lưu
 * trữ phía máy chủ trở thành trang trí: giao diện luôn khởi động bằng tiếng Việt, và câu "đăng nhập
 * máy khác vẫn giữ ngôn ngữ" là sai.
 *
 * ⚠ THỨ TỰ ƯU TIÊN: lựa chọn khách vừa bấm khi CHƯA đăng nhập (`localStorage['rs:lang']`, do
 *   `<LanguagePicker>` ghi ở `/login` · `/ncc/login` · `/tra-cuu`) THẮNG `me.lang` — nó mới là thao
 *   tác gần nhất của con người. Chuyển nó lên phiên rồi mới xoá chỗ giữ tạm; PUT hỏng thì GIỮ LẠI
 *   để lần vào sau thử tiếp, không im lặng vứt đi.
 */
const readTempLang = (): string | null => {
  try { return localStorage.getItem(TEMP_LANG_KEY) } catch { return null }
}
const clearTempLang = (): void => {
  try { localStorage.removeItem(TEMP_LANG_KEY) } catch { /* chế độ riêng tư */ }
}

const syncLanguage = async (me: Me): Promise<void> => {
  const temp = readTempLang()
  if (temp !== null && temp !== me.lang) {
    const r = await fetch('/api/v1/prefs/lang', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ lang: temp }),
    }).catch(() => null)
    if (r?.ok === true) {
      clearTempLang()
      await switchLanguage(temp as Me['lang'])
      return
    }
    // Máy chủ từ chối (VD ngôn ngữ rác ai đó tự nhét vào localStorage) ⇒ bỏ chỗ giữ tạm đi, đừng
    // thử lại vô hạn mỗi lần vào app.
    if (r !== null) clearTempLang()
  } else if (temp !== null) {
    clearTempLang()   // trùng với ngôn ngữ đang lưu ở máy chủ: không có gì để chuyển
  }
  await switchLanguage(me.lang)
}

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [checkError, setCheckError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  /** Số lượt hỏi `/auth/me` — tăng là effect hỏi lại (tự thử lại hoặc bấm "Thử lại ngay"). */
  const [attempt, setAttempt] = useState(0)

  /** Áp một kết quả vào state — dùng chung cho effect và `refresh()`. Trả `Me` khi đã vào được. */
  const apply = useCallback((r: MeResult): Me | null => {
    setLoading(false)
    if (r.kind === 'error') { setCheckError(r.status); return null }
    setCheckError(null)
    const m = r.kind === 'me' ? r.me : null
    setMe(m)
    return m
  }, [])

  const refresh = useCallback(async (): Promise<void> => {
    const m = apply(await fetchMe())
    if (m) await syncLanguage(m)
  }, [apply])

  // Ghi state trong callback của promise (đúng khuôn `routes/index.tsx`), KHÔNG gọi thẳng trong
  // thân effect — luật lint `react-hooks/set-state-in-effect` chặn, xem quyết định B1.
  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined
    void fetchMe().then((r) => {
      if (!alive) return
      const m = apply(r)
      // Lỗi khác 401 ⇒ tự hỏi lại theo nhịp giãn dần; hết nhịp thì chờ người bấm "Thử lại ngay".
      const delay = RETRY_DELAYS_MS[attempt]
      if (r.kind === 'error' && delay !== undefined) timer = setTimeout(() => { setAttempt((a) => a + 1) }, delay)
      // ⚠ PHẢI CÓ `.catch`. `void promise` không có nhánh bắt lỗi là một **unhandled rejection**:
      //   ở trình duyệt nó chỉ là một dòng đỏ trong console, nhưng ở vitest nó làm HỎNG CẢ LƯỢT
      //   CHẠY dù mọi ca đều xanh — đã tái lập thật ở `pnpm ci:test` (4 unhandled errors, exit 1).
      //   Về nghiệp vụ thì cũng đúng như vậy: đổi ngôn ngữ hỏng KHÔNG được phép làm trắng màn hình
      //   của kế toán đang chốt sổ; giữ nguyên ngôn ngữ hiện tại và ghi log là hành vi đúng — cùng
      //   lý lẽ với `missingKeyHandler` ở `@rs/ui/i18n`.
      if (m) {
        void syncLanguage(m).catch((e: unknown) => {
          console.error('Không đổi được ngôn ngữ theo hồ sơ người dùng — giữ ngôn ngữ hiện tại.', e)
        })
      }
    })
    return () => { alive = false; if (timer !== undefined) clearTimeout(timer) }
  }, [attempt, apply])

  /* ⭐ HẾT PHIÊN GIỮA CHỪNG (L18): mọi lệnh qua `call()` gặp 401 phát `SESSION_EXPIRED_EVENT`. Đang
     có phiên mà nghe thấy ⇒ bỏ phiên ở trình duyệt, router vẽ màn đăng nhập NGAY tại chỗ (thay cho
     cả màn lẫn hộp đang mở — bản cũ cũng đóng hộp và về `/login`, `auth.tsx:103-110`), kèm câu
     "phiên đã hết hạn" để người dùng biết vì sao. */
  useEffect(() => {
    // Chưa đăng nhập thì không màn nào gọi `call()` (router chỉ vẽ màn đăng nhập), nên không có ca
    // "báo hết phiên oan" cho người chưa từng vào.
    const onExpired = (): void => { setExpired(true); setMe(null) }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    return () => { window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired) }
  }, [])

  const retry = useCallback((): void => { setAttempt((a) => a + 1) }, [])

  /** Huỷ phiên rs-erp TRƯỚC, rồi mới để FADO SSO đóng phiên của nó (RP-Initiated Logout, E17). Phiên mở bằng TÀI KHOẢN
   *  (E13 sửa 19/10) không có gì bên FADO để đóng — máy chủ trả `ssoLogoutUrl: null`, router vẽ lại màn đăng nhập. */
  const logout = useCallback(async (): Promise<void> => {
    const r = await fetch('/api/v1/auth/logout', { method: 'POST' })
    setMe(null)
    const { ssoLogoutUrl } = (await r.json()) as { ssoLogoutUrl: string | null }
    if (ssoLogoutUrl) location.assign(ssoLogoutUrl)
  }, [])

  return (
    <AuthCtx.Provider value={{ me, loading, checkError, expired, retry, refresh, logout }}>
      {children}
    </AuthCtx.Provider>
  )
}

export const useAuth = (): Ctx => {
  const c = useContext(AuthCtx)
  if (!c) throw new Error('useAuth phải nằm trong <AuthProvider>')
  return c
}
